import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { API } from "../../shared/api/Api";
import { selectUserInformations } from "../../features/authSlice";
import { showToast } from "../../utils/utils";

const STATUS_META = {
  pending_payment: {
    label: "Payment required",
    color: "text-amber-900 bg-amber-50 border-amber-200/90",
    desc: "Choose a payment method and submit it for review. You can start the exam after an admin verifies your payment.",
    icon: "💳",
  },
  pending_attempt: {
    label: "Ready to start",
    color: "text-emerald-900 bg-emerald-50 border-emerald-200/90",
    desc: "Your placement test is unlocked. Open the exam app when you are ready to begin.",
    icon: "🎯",
  },
  awaiting_review: {
    label: "Awaiting review",
    color: "text-blue-900 bg-blue-50 border-blue-200/90",
    desc: "Your attempt has been submitted and is waiting in the review queue.",
    icon: "⏳",
  },
  under_review: {
    label: "Under review",
    color: "text-violet-900 bg-violet-50 border-violet-200/90",
    desc: "An administrator is reviewing your placement result.",
    icon: "🔍",
  },
  course_assigned: {
    label: "Course assigned",
    color: "text-emerald-900 bg-emerald-50 border-emerald-200/90",
    desc: "You have been assigned to a course based on your placement outcome.",
    icon: "✅",
  },
  rejected: {
    label: "Not passed",
    color: "text-red-900 bg-red-50 border-red-200/90",
    desc: "You did not meet the minimum requirements for this placement. Please contact support if you need help.",
    icon: "❌",
  },
};

const btnPrimary =
  "w-full inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3.5 text-sm font-semibold text-white shadow-sm transition hover:opacity-95 active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:opacity-60 disabled:pointer-events-none";
const btnAmber =
  "w-full inline-flex items-center justify-center gap-2 rounded-xl bg-amber-500 px-4 py-3.5 text-sm font-semibold text-white shadow-sm transition hover:bg-amber-600 active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2";
const btnSuccess =
  "w-full inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-3.5 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2";

function DetailsLoading() {
  return (
    <div className="min-h-[50vh] max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 animate-pulse">
      <div className="h-4 w-40 rounded bg-gray-200 mb-8" />
      <div className="rounded-2xl bg-gray-200/80 h-48 sm:h-56 mb-6" />
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="h-24 rounded-xl bg-gray-100" />
          <div className="h-40 rounded-xl bg-gray-100" />
        </div>
        <div className="h-48 rounded-xl bg-gray-100" />
      </div>
    </div>
  );
}

const PublicPlacementTestDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useSelector(selectUserInformations);
  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const buildExamsPlacementUrl = (testId) => `${API.examsAppUrl}/placement-test/${testId}`;
  const price = Number(test?.price ?? 0);

  useEffect(() => {
    const fetchDetails = async () => {
      setLoading(true);
      try {
        const headers = user?.token ? { Authorization: `Bearer ${user.token}` } : {};
        const { data } = await axios.get(`${API.PlacementTests.get}/${id}`, { headers });
        if (data.status === 200) {
          setTest(data.data);
        } else {
          showToast("Error", "Failed to fetch placement test details.", "error");
        }
      } catch (error) {
        showToast("Error", "Failed to fetch placement test details.", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [id, user?.token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <DetailsLoading />
      </div>
    );
  }

  if (!test) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex flex-col items-center justify-center px-4 py-16">
        <span className="text-5xl" aria-hidden>
          🔍
        </span>
        <p className="mt-4 text-xl font-semibold text-gray-900 text-center">Test not found</p>
        <p className="mt-2 text-sm text-gray-600 text-center max-w-sm">
          This placement test may have been removed or the link is incorrect.
        </p>
        <button
          type="button"
          onClick={() => navigate("/placement-tests")}
          className="mt-8 inline-flex items-center rounded-xl border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-gray-800 shadow-sm transition hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        >
          ← Back to placement tests
        </button>
      </div>
    );
  }

  const enrollment = test.enrollment ?? test.pivot;
  const resultStatus = enrollment?.result_status;
  const statusMeta = resultStatus ? STATUS_META[resultStatus] : null;
  const isEnrolled = test.is_enrolled || !!enrollment;
  const needsPlacementFee = !test.is_free && price > 0;
  const paymentNotVerified = enrollment?.payment_status !== "paid";
  const goToPaymentPage = () => navigate(`/placement-tests/${id}/payment`, { state: { test } });

  const renderCTA = () => {
    if (!user) {
      return (
        <button
          type="button"
          onClick={() => navigate("/login", { state: { redirect: `/placement-tests/${id}` } })}
          className={btnPrimary}
        >
          Log in to continue
        </button>
      );
    }

    if (!isEnrolled) {
      if (needsPlacementFee) {
        return (
          <button
            type="button"
            onClick={() => {
              if (!user?.student?.id) {
                showToast("Error", "You must be logged in as a student.", "error");
                return;
              }
              goToPaymentPage();
            }}
            className={btnPrimary}
          >
            {test.type === "pre_enrollment"
              ? "Start placement test — choose payment"
              : `Start placement test — pay $${price.toFixed(2)}`}
          </button>
        );
      }

      return (
        <button
          type="button"
          onClick={async () => {
            if (!user?.student?.id) {
              showToast("Error", "You must be logged in as a student.", "error");
              return;
            }
            setEnrolling(true);
            try {
              const { data } = await axios.post(
                API.PlacementTests.enroll,
                { placement_test_id: Number(id), student_id: user.student.id },
                { headers: { Authorization: `Bearer ${user.token}` } }
              );
              if (data.status !== 200 && data.status !== 201) {
                showToast("Error", data?.message || "Unable to enroll right now.", "error");
                return;
              }

              showToast("Success", "Enrollment completed. You can start your placement exam.", "success");
              window.open(buildExamsPlacementUrl(id), "_blank", "noopener,noreferrer");
              navigate(0);
            } catch (error) {
              showToast("Error", error.response?.data?.message || "Unable to enroll right now.", "error");
            } finally {
              setEnrolling(false);
            }
          }}
          disabled={enrolling}
          className={btnPrimary}
        >
          {enrolling ? "Processing…" : "Enroll & take placement test"}
        </button>
      );
    }

    if (resultStatus === "pending_payment") {
      return (
        <button type="button" onClick={goToPaymentPage} className={btnAmber}>
          Choose payment method
        </button>
      );
    }

    if (resultStatus === "pending_attempt") {
      if (needsPlacementFee && paymentNotVerified) {
        return (
          <button type="button" onClick={goToPaymentPage} className={btnAmber}>
            Choose payment method
          </button>
        );
      }
      return (
        <a
          href={buildExamsPlacementUrl(id)}
          target="_blank"
          rel="noopener noreferrer"
          className={btnSuccess}
        >
          Start placement test
          <span aria-hidden>↗</span>
        </a>
      );
    }

    return null;
  };

  const cta = renderCTA();
  const passPct = Number(test.passing_score ?? test?.exam?.passing_score ?? 0);
  const fullMarks = Number(test.full_marks ?? test?.exam?.full_marks ?? 0);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50/90">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10 lg:py-12">
        <button
          type="button"
          onClick={() => navigate("/placement-tests")}
          className="group mb-6 sm:mb-8 inline-flex items-center gap-2 text-sm font-medium text-gray-600 transition hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg px-1 py-1 -ml-1"
        >
          <span className="transition group-hover:-translate-x-0.5" aria-hidden>
            ←
          </span>
          All placement tests
        </button>

        <div className="flex flex-col lg:grid lg:grid-cols-12 lg:gap-8 lg:items-start">
          {/* Main column */}
          <div className="lg:col-span-8 space-y-6 sm:space-y-8 order-1">
            <div className="overflow-hidden rounded-2xl border border-gray-200/90 bg-white shadow-sm">
              <div className="bg-gradient-to-br from-primary via-blue-600 to-blue-800 px-5 py-8 sm:px-8 sm:py-10 text-white">
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <span className="inline-flex items-center rounded-full border border-white/25 bg-white/15 px-3 py-1 text-xs font-semibold backdrop-blur-sm">
                    {test.type === "pre_enrollment" ? "Pre-enrollment" : "Standalone"}
                  </span>
                  <span className="inline-flex items-center rounded-full border border-white/25 bg-white/15 px-3 py-1 text-xs font-semibold backdrop-blur-sm">
                    {price > 0 ? `$${price.toFixed(2)}` : "Free"}
                  </span>
                </div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-tight">{test.title}</h1>
                {test.description && (
                  <p className="mt-3 text-sm sm:text-base text-white/85 leading-relaxed max-w-3xl">{test.description}</p>
                )}

                <dl className="mt-6 sm:mt-8 grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 text-sm">
                  <div className="rounded-xl bg-white/10 px-3 py-3 sm:px-4 border border-white/10">
                    <dt className="text-white/70 text-xs font-medium uppercase tracking-wide">Duration</dt>
                    <dd className="mt-1 font-semibold tabular-nums">{test.duration_minutes} min</dd>
                  </div>
                  <div className="rounded-xl bg-white/10 px-3 py-3 sm:px-4 border border-white/10">
                    <dt className="text-white/70 text-xs font-medium uppercase tracking-wide">Pass score</dt>
                    <dd className="mt-1 font-semibold tabular-nums">{passPct}%</dd>
                  </div>
                  <div className="rounded-xl bg-white/10 px-3 py-3 sm:px-4 border border-white/10 col-span-2 sm:col-span-1">
                    <dt className="text-white/70 text-xs font-medium uppercase tracking-wide">Full marks</dt>
                    <dd className="mt-1 font-semibold tabular-nums">{fullMarks}</dd>
                  </div>
                  {test.max_attempts ? (
                    <div className="rounded-xl bg-white/10 px-3 py-3 sm:px-4 border border-white/10 col-span-2 sm:col-span-1">
                      <dt className="text-white/70 text-xs font-medium uppercase tracking-wide">Attempts</dt>
                      <dd className="mt-1 font-semibold tabular-nums">{test.max_attempts}</dd>
                    </div>
                  ) : null}
                </dl>

                {test.course?.title && (
                  <p className="mt-5 sm:mt-6 text-sm text-white/80 flex flex-wrap items-center gap-2">
                    <span className="font-medium text-white/90">Linked course:</span>
                    <span className="rounded-lg bg-black/20 px-2.5 py-1 text-white font-medium">{test.course.title}</span>
                  </p>
                )}
              </div>

              <div className="p-5 sm:p-8 space-y-6 sm:space-y-8">
                {statusMeta && (
                  <div
                    className={`flex gap-3 sm:gap-4 rounded-xl border p-4 sm:p-5 text-sm ${statusMeta.color}`}
                    role="status"
                  >
                    <span className="text-2xl shrink-0 leading-none" aria-hidden>
                      {statusMeta.icon}
                    </span>
                    <div className="min-w-0">
                      <p className="font-semibold text-base">{statusMeta.label}</p>
                      <p className="mt-1.5 leading-relaxed opacity-90">{statusMeta.desc}</p>
                      {enrollment?.admin_notes && (
                        <p className="mt-2 text-xs sm:text-sm italic opacity-80 border-t border-current/10 pt-2">
                          &ldquo;{enrollment.admin_notes}&rdquo;
                        </p>
                      )}
                      {enrollment?.score != null && (
                        <p className="mt-2 font-semibold tabular-nums">Your score: {enrollment.score}</p>
                      )}
                    </div>
                  </div>
                )}

                {!isEnrolled && (
                  <section className="rounded-xl border border-gray-100 bg-slate-50/80 p-5 sm:p-6">
                    <h2 className="text-base sm:text-lg font-bold text-gray-900">How it works</h2>
                    <ol className="mt-4 space-y-4 sm:space-y-5">
                      {(test.type === "pre_enrollment"
                        ? [
                            {
                              title: "Start with payment (if there is a fee)",
                              desc:
                                price > 0
                                  ? `Choose a method and submit $${price.toFixed(2)} for admin review before you can take the exam.`
                                  : "Enroll and begin the exam right away — no placement fee.",
                            },
                            {
                              title: "Take the placement test",
                              desc: "Complete the exam in our secure test app when your status shows as ready.",
                            },
                            {
                              title: "Course access",
                              desc: "If you pass, follow the instructions you receive to continue enrollment in the linked course.",
                            },
                          ]
                        : [
                            {
                              title: "Choose payment (if required)",
                              desc:
                                price > 0
                                  ? `Submit $${price.toFixed(2)} using your preferred method for admin verification.`
                                  : "Enroll for free and continue immediately.",
                            },
                            {
                              title: "Take the exam",
                              desc: "Use the exam app when your enrollment is ready.",
                            },
                            {
                              title: "Get your result",
                              desc: "Track your status here and in your student dashboard after review.",
                            },
                          ]
                      ).map((step, idx) => (
                        <li key={step.title} className="flex gap-3 sm:gap-4">
                          <span
                            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-white shadow-sm"
                            aria-hidden
                          >
                            {idx + 1}
                          </span>
                          <div className="min-w-0 pt-0.5">
                            <p className="font-semibold text-gray-900 text-sm sm:text-base">{step.title}</p>
                            <p className="mt-1 text-xs sm:text-sm text-gray-600 leading-relaxed">{step.desc}</p>
                          </div>
                        </li>
                      ))}
                    </ol>
                  </section>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar / CTA */}
          <aside className="lg:col-span-4 mt-8 lg:mt-0 order-2 lg:sticky lg:top-6 self-start w-full">
            <div className="rounded-2xl border border-gray-200/90 bg-white p-5 sm:p-6 shadow-sm space-y-5">
              <div>
                <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500">Summary</h2>
                <ul className="mt-3 space-y-2.5 text-sm text-gray-700">
                  <li className="flex justify-between gap-3">
                    <span className="text-gray-500">Fee</span>
                    <span className="font-semibold tabular-nums">{price > 0 ? `$${price.toFixed(2)}` : "Free"}</span>
                  </li>
                  <li className="flex justify-between gap-3">
                    <span className="text-gray-500">Type</span>
                    <span className="font-medium text-right">
                      {test.type === "pre_enrollment" ? "Course placement" : "Standalone"}
                    </span>
                  </li>
                  <li className="flex justify-between gap-3">
                    <span className="text-gray-500">Your status</span>
                    <span className="font-medium text-right">
                      {!user
                        ? "Not signed in"
                        : !isEnrolled
                          ? "Not enrolled"
                          : statusMeta?.label ?? "Enrolled"}
                    </span>
                  </li>
                </ul>
              </div>

              <div className="border-t border-gray-100 pt-5 space-y-3">{cta}</div>

              {!cta && isEnrolled && (
                <p className="text-xs text-gray-500 leading-relaxed">
                  There is no further action on this page. Check your dashboard or email for updates.
                </p>
              )}

              {user && test.type === "pre_enrollment" && (
                <p className="text-xs text-gray-500 leading-relaxed border-t border-gray-100 pt-4">
                  Tip: after payment is verified, use <strong className="text-gray-700">Start placement test</strong> to
                  open the exam in a new tab.
                </p>
              )}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default PublicPlacementTestDetailsPage;
